import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { IAuditListItem } from 'src/types/audit';
import {
  IBadDebtMatch,
  ILoanAgreementItem,
  ILoanPaymentPayload,
  ILoanBorrowerPayload,
  ILoanAgreementListItem,
  ICreateLoanAgreementPayload,
  IUpdateLoanAgreementPayload,
} from 'src/types/loan-agreement';

import axios from '../../utils/axios';

type Status = 'idle' | 'loading' | 'success' | 'failure';

interface IInitialState {
  status: Status;
  error: string | undefined;
  agreements: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: ILoanAgreementListItem[];
  };
  agreement: {
    status: Status;
    error: string | undefined;
    data: ILoanAgreementItem | null;
  };
  auditLog: {
    status: Status;
    error: string | undefined;
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IAuditListItem[];
  };
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  agreements: {
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    items: [],
  },
  agreement: {
    status: 'idle',
    error: undefined,
    data: null,
  },
  auditLog: {
    status: 'idle',
    error: undefined,
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    items: [],
  },
};

const loanAgreementSlice = createSlice({
  name: 'loanAgreement',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getLoanAgreements.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getLoanAgreements.fulfilled, (state, action) => {
        state.status = 'success';
        state.agreements = action.payload;
      })
      .addCase(getLoanAgreements.rejected, (state, action) => {
        state.agreements = initialState.agreements;
        state.status = 'failure';
        state.error = action.error.message;
      })

      .addCase(getLoanAgreementById.pending, (state) => {
        state.agreement.status = 'loading';
      })
      .addCase(getLoanAgreementById.fulfilled, (state, action) => {
        state.agreement.status = 'success';
        state.agreement.data = action.payload;
        state.agreement.error = undefined;
      })
      .addCase(getLoanAgreementById.rejected, (state, action) => {
        state.agreement.status = 'failure';
        state.agreement.data = null;
        state.agreement.error = action.error.message;
      })

      // Every agreement-level write returns the refreshed agreement.
      .addCase(updateLoanAgreement.fulfilled, (state, action) => {
        state.agreement.data = action.payload;
      })
      .addCase(markLoanBadDebt.fulfilled, (state, action) => {
        state.agreement.data = action.payload;
      })
      .addCase(reverseLoanBadDebt.fulfilled, (state, action) => {
        state.agreement.data = action.payload;
      })

      .addCase(getLoanAgreementAuditLog.pending, (state) => {
        state.auditLog.status = 'loading';
      })
      .addCase(getLoanAgreementAuditLog.fulfilled, (state, action) => {
        state.auditLog = { ...action.payload, status: 'success', error: undefined };
      })
      .addCase(getLoanAgreementAuditLog.rejected, (state, action) => {
        state.auditLog.status = 'failure';
        state.auditLog.error = action.error.message;
      });
  },
});

export default loanAgreementSlice.reducer;

export const getLoanAgreements = createAsyncThunk(
  'loanAgreement/list',
  async (params: {
    page: number | null;
    limit: number | null;
    status?: string | null;
    borrowerType?: string | null;
    employee?: string | null;
    generalSearch?: string | null;
    overdue?: boolean;
    sortBy: string;
    sort: 'asc' | 'desc';
  }) => {
    try {
      const response = await axios.get('loan-agreement', {
        params: {
          // page=0&limit=0 returns every matching row.
          page: params.page !== null ? params.page + 1 : 0,
          limit: params.limit ?? 0,
          status: params.status || undefined,
          borrowerType: params.borrowerType || undefined,
          employee: params.employee || undefined,
          generalSearch: params.generalSearch || undefined,
          overdue: params.overdue || undefined,
          sortBy: params.sortBy,
          sort: params.sort,
        },
      });

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

/**
 * Every matching row, ignoring pagination - used by Print. Not stored in
 * the slice so it doesn't disturb the paginated table.
 */
export async function fetchAllLoanAgreements(params: {
  status?: string | null;
  borrowerType?: string | null;
  employee?: string | null;
  generalSearch?: string | null;
  overdue?: boolean;
  sortBy: string;
  sort: 'asc' | 'desc';
}): Promise<ILoanAgreementListItem[]> {
  const response = await axios.get('loan-agreement', {
    params: {
      page: 0,
      limit: 0,
      status: params.status || undefined,
      borrowerType: params.borrowerType || undefined,
      employee: params.employee || undefined,
      generalSearch: params.generalSearch || undefined,
      overdue: params.overdue || undefined,
      sortBy: params.sortBy,
      sort: params.sort,
    },
  });

  return response.data.data.items;
}

export async function checkLoanBadDebt(
  params: ILoanBorrowerPayload & { excludeAgreement?: string }
): Promise<IBadDebtMatch[]> {
  const response = await axios.post('loan-agreement/bad-debt-check', params);
  return response.data.data ?? [];
}

export const getLoanAgreementById = createAsyncThunk(
  'loanAgreement/getById',
  async (params: { id: string }) => {
    try {
      const response = await axios.get(`loan-agreement/${params.id}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const createLoanAgreement = createAsyncThunk(
  'loanAgreement/create',
  async (params: { agreement: ICreateLoanAgreementPayload }) => {
    try {
      const response = await axios.post('loan-agreement', params.agreement);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const updateLoanAgreement = createAsyncThunk(
  'loanAgreement/update',
  async (params: { id: string; agreement: IUpdateLoanAgreementPayload }) => {
    try {
      const response = await axios.patch(`loan-agreement/${params.id}`, params.agreement);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deleteLoanAgreement = createAsyncThunk(
  'loanAgreement/delete',
  async (params: { id: string }) => {
    try {
      const response = await axios.delete(`loan-agreement/${params.id}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const markLoanBadDebt = createAsyncThunk(
  'loanAgreement/markBadDebt',
  async (params: { id: string; reason: string }) => {
    try {
      const response = await axios.post(`loan-agreement/${params.id}/bad-debt`, {
        reason: params.reason,
      });

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const reverseLoanBadDebt = createAsyncThunk(
  'loanAgreement/reverseBadDebt',
  async (params: { id: string; reason: string }) => {
    try {
      const response = await axios.post(`loan-agreement/${params.id}/bad-debt/reverse`, {
        reason: params.reason,
      });

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const createLoanPayment = createAsyncThunk(
  'loanAgreement/createPayment',
  async (params: { id: string; payment: ILoanPaymentPayload }) => {
    try {
      const response = await axios.post(`loan-agreement/${params.id}/payment`, params.payment);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const updateLoanPayment = createAsyncThunk(
  'loanAgreement/updatePayment',
  async (params: { id: string; paymentId: string; payment: Partial<ILoanPaymentPayload> }) => {
    try {
      const response = await axios.patch(
        `loan-agreement/${params.id}/payment/${params.paymentId}`,
        params.payment
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deleteLoanPayment = createAsyncThunk(
  'loanAgreement/deletePayment',
  async (params: { id: string; paymentId: string }) => {
    try {
      const response = await axios.delete(
        `loan-agreement/${params.id}/payment/${params.paymentId}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getLoanAgreementAuditLog = createAsyncThunk(
  'loanAgreement/auditLog',
  async (params: { id: string; page: number; limit: number }) => {
    try {
      const response = await axios.get(
        `audit/loan-agreement/${params.id}?page=${params.page}&limit=${params.limit}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);
